import { Button, Flex, Grid, Loader, TextField } from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useContext, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import {
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";

import {
  costCentersByCompanyID,
  getServiceOrder,
  listUsers,
  serviceOrderLogsByCompanyID,
} from "graphql/queries";
import { LayoutContext } from "layout/context/LayoutContext";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";
import DateRangeForm from "components/utils/DateRangeForm";
import moment from "moment";
import Empty from "components/utils/Empty";
import { serviceOrderStatus } from "config/constants";
import { groupBy } from "components/utils/functions";
import usePermissions from "hooks/usePermission";
import {
  computeByUserPercentages,
  computeGroupedDurations,
  exportTableToExcel,
} from "components/payrollReport/payrollReportFunctions";

const PayrollReport = () => {
  const { me } = useContext(LayoutContext);
  const canDownload = usePermissions("payroll-report:download");
  const [modalDateVisible, setModalDateVisible] = useState(false);
  const [users, setUsers] = useState({ data: [], loading: false });
  const [costCenters, setCostCenters] = useState({ data: [], loading: false });
  const [percentages, setPercentages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
  });
  const [filterBy, setFilterBy] = useState({
    from: moment().startOf("day").toDate(),
    to: moment().endOf("day").toDate(),
  });
  const tableColumns = useMemo(() => {
    let cols = [
      {
        key: "costCenter",
        label: strings.table.headers.costCenter,
        _style: { width: "5%" },
      },
      {
        key: "costCenterName",
        label: strings.table.headers.costCenterName,
        _style: { width: "15%" },
      },
    ];

    users.data?.forEach((user) => {
      cols.push({
        key: `var_${user.id}`,
        label: user.name,
        _style: { width: "5%", backgroundColor: "#a0c8f0ff" },
      });
    });
    return cols;
  }, [users.data]);
  const tableItems = useMemo(() => {
    const centers = costCenters?.data || [];
    const usersList = users?.data || [];
    const percList = Array.isArray(percentages) ? percentages : [];

    return centers.map((costCenter) => {
      const cols = {};

      for (const user of usersList) {
        const userGroup = percList.find(
          (p) => String(p.userExecutor) === String(user.id)
        );
        const detail = userGroup?.details?.find(
          (d) => String(d.costCenterID) === String(costCenter.id)
        );

        cols[`var_${user.id}`] = detail ? detail.percentage : "";
      }

      return {
        costCenter: costCenter.code,
        costCenterName: costCenter.name,
        ...cols,
      };
    });
  }, [costCenters.data, percentages, users.data]);

  const onSubmitDateRange = (date) => {
    const { from, to } = date;
    setFilterBy({ from, to });
    setModalDateVisible(false);
  };

  const onSubmit = async () => {
    try {
      setLoading(true);
      await fetchUsers();
      await fetchCostCenters(searchBy.company.id || me.companyID);
      await fetchServiceOrders(searchBy.company.id || me.companyID);
    } finally {
      setLoading(false);
    }
  };

  async function fetchUsers() {
    let filterObj = {};
    try {
      setUsers({ data: [], loading: true });

      if (me.companyID)
        filterObj = { ...filterObj, or: [{ companyID: { eq: me.companyID } }] };
      if (me.managedCompanies.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ companyID: { eq: cp } })),
          ],
        };

      const usersData = await API.graphql(
        graphqlOperation(listUsers, {
          filter: {
            ...filterObj,
            isTechnician: { eq: true },
          },
        })
      );
      const usersItems = usersData.data.listUsers.items;
      setUsers((prev) => ({ ...prev, data: usersItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchUsers);
    } finally {
      setUsers((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchCostCenters(companyID) {
    try {
      if (!companyID) return;
      setCostCenters({ data: [], loading: true });

      const costCentersData = await API.graphql(
        graphqlOperation(costCentersByCompanyID, {
          companyID,
        })
      );
      const costCentersItems =
        costCentersData.data.costCentersByCompanyID.items;
      setCostCenters((prev) => ({ ...prev, data: costCentersItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchCostCenters);
    } finally {
      setCostCenters((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchServiceOrders(companyID) {
    try {
      if (!companyID) return;
      const serviceOrdersData = await API.graphql(
        graphqlOperation(serviceOrderLogsByCompanyID, {
          companyID,
          filter: {
            createdAt: {
              between: [
                moment(filterBy.from).startOf("day").toISOString(),
                moment(filterBy.to).endOf("day").toISOString(),
              ],
            },
            or: [
              { status: { eq: serviceOrderStatus.executing } },
              { status: { eq: serviceOrderStatus.paused } },
              { status: { eq: serviceOrderStatus.executed } },
            ],
          },
        })
      );
      const serviceOrdersItems =
        serviceOrdersData.data.serviceOrderLogsByCompanyID.items;

      let groupedServiceOrders = computeGroupedDurations(serviceOrdersItems, {
        countRunning: false,
      });

      groupedServiceOrders = await Promise.all(
        groupedServiceOrders?.map(async (serviceOrder) => {
          const modelData = await API.graphql(
            graphqlOperation(getServiceOrder, {
              id: serviceOrder.serviceOrderID,
            })
          );
          const modelItem = modelData.data.getServiceOrder;

          return {
            ...serviceOrder,
            userExecutor: modelItem.userExecutor,
            costCenterID: modelItem.costCenterID,
          };
        }) ?? []
      );

      const groupedByCostCenterAndUser = groupBy(
        groupedServiceOrders,
        (o) => `${o.userExecutor}::${o.costCenterID}`
      );

      const durationData = Object.keys(groupedByCostCenterAndUser).map(
        (key) => {
          const [userExecutor, costCenterID] = key.split("::");
          const items = groupedByCostCenterAndUser[key];
          const totalDuration = items.reduce(
            (acc, item) => acc + item.duration,
            0
          );
          return {
            userExecutor,
            costCenterID,
            totalDuration,
          };
        }
      );

      const results = computeByUserPercentages(durationData, {
        asPercentString: true,
        decimals: 1,
      });

      setPercentages(results);
    } catch (err) {
      console.log("Error fetching service orders:", err);
      toast.error(strings.responses.errorFetchServiceOrders);
    }
  }

  return (
    <div>
      <span className="fs-3">{strings.title}</span>

      <div className="p-3">
        <>
          <Grid
            className="mb-3"
            rowGap="15px"
            columnGap="15px"
            alignItems="center"
            templateColumns={{ base: "1fr", xxl: "4fr 4fr 4fr 2fr" }}
          >
            <CompanyBranchFilter
              onlyCompany
              searchBy={searchBy}
              setSearchBy={setSearchBy}
            />
            <div style={styles.searchBy}>
              <TextField
                readOnly
                value={`${moment(filterBy.from).format(
                  "YYYY-MM-DD HH:mm"
                )} - ${moment(filterBy.to).format("YYYY-MM-DD HH:mm")}`}
                onClick={() => setModalDateVisible(true)}
                outerEndComponent={
                  <Button onClick={() => setModalDateVisible(true)}>
                    <EditCalendarIcon fontSize="small" />
                  </Button>
                }
              />
            </div>
            <Flex gap="10px">
              <Button
                disabled={
                  !searchBy.company.id || !filterBy.from || !filterBy.to
                }
                style={styles.button}
                children={strings.generate}
                variation="primary"
                onClick={onSubmit}
              />
              {tableItems?.length > 0 &&
                tableColumns?.length > 0 &&
                canDownload &&
                !loading && (
                  <Button
                    style={styles.button}
                    children={strings.download}
                    variation="link"
                    onClick={() => exportTableToExcel(tableItems, users.data)}
                  />
                )}
            </Flex>
          </Grid>
          {loading ? (
            <Loader />
          ) : tableItems?.length <= 0 || tableColumns?.length <= 0 ? (
            <Empty />
          ) : (
            <CTable
              className="table-centered"
              items={tableItems}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
          )}
        </>
      </div>
      <CModal
        alignment="center"
        visible={modalDateVisible}
        onClose={() => setModalDateVisible(false)}
        portal={false}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>{strings.selectDateRange}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <DateRangeForm
            initialValues={{
              from: filterBy.from && moment(filterBy.from).toDate(),
              to: filterBy.to && moment(filterBy.to).toDate(),
            }}
            onSubmit={onSubmitDateRange}
            withHours
          />
        </CModalBody>
      </CModal>
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Reporte de Nómina",
    selectDateRange: "Seleccionar Rango de Fecha",
    responses: {
      errorFetch: "Error al consultar datos",
      errorFetchUsers: "Error al consultar usuarios",
      errorFetchCostCenters: "Error al consultar centros de costo",
      errorFetchServiceOrders: "Error al consultar órdenes de servicio",
    },
    table: {
      headers: {
        costCenter: "Centro de Costo",
        costCenterName: "Nombre Centro de Costo",
      },
    },
    fields: {
      variable: {
        label: "Variable",
        placeholder: "Seleccione una variable",
      },
      time: {
        label: "Tiempo",
        placeholder: "Selecciona un tiempo",
        options: {
          lastValue: "Último valor",
          hour: "Última hora",
          day: "Último día",
          week: "Última semana",
          month: "Último mes",
        },
      },
    },
    cards: {
      companies: "Empresas",
      branches: "Sedes",
      variables: "Variables",
    },
    generate: "Generar Reporte",
    download: "Descargar",
  },
});

const styles = {
  searchBy: {
    marginTop: "-10px",
  },
  button: {
    justifySelf: "start",
  },
};

export default PayrollReport;
